import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { SHOPIFY_TEMPLATE_TYPES } from '~/constants/shopify';
import * as DataDogLogs from '~/lib/datadog/datadog';
import {
  registerSectionRerenderCallback,
  rerenderSections,
  rerenderSectionsByType,
  unregisterSectionRerenderCallback,
} from '~/utils/section-rerenderer';
import { vueToTrackJsErrorHandler } from '~/utils/trackjs/trackjs';

gsap.registerPlugin(ScrollTrigger);

/**
 * Base class for all sections.
 *
 * Shopify Design Mode:
 *  - Add common logic to listen for the 'shopify:section:load' and 'shopify:section:unload' events.
 *
 * Error Handling:
 *  - The error handler for the Vue app is configured in the _configureErrorMonitoring method.
 *  - Make sure to provide a "name:" when the Vue app is created ie: createApp({ name: 'HomepageHero' }).
 *
 * Animation:
 *  - Uses GSAP ScrollTrigger to control animations based on scroll position
 *  - Each section can define its own animation timeline and ScrollTrigger settings
 */
export class BaseSection {
  static instances = new Map();

  constructor(selector) {
    const containers = document.querySelectorAll(selector);
    if (!containers.length) return;

    containers.forEach((container) => {
      const sectionId = container.getAttribute('data-section-id');
      if (!sectionId) return;

      // Set the template name for the section
      this.templateName = !this.templateName
        ? container.getAttribute('data-section-template-name')
        : this.templateName;

      const instanceData = {
        animation: null,
        container,
        initialized: false,
        scrollTrigger: null,
        section: this,
      };

      if (!BaseSection.instances.has(this.constructor.name)) {
        BaseSection.instances.set(this.constructor.name, new Map());
      }
      BaseSection.instances.get(this.constructor.name).set(sectionId, instanceData);

      this._initInstance(instanceData);
    });

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', (event) => {
        const container = event.target.querySelector(selector);
        if (!container) return;

        const sectionId = container.getAttribute('data-section-id');
        const sectionMap = BaseSection.instances.get(this.constructor.name);

        if (sectionMap && sectionId) {
          const instanceData = {
            animation: null,
            container,
            initialized: false,
            scrollTrigger: null,
            section: this,
          };
          sectionMap.set(sectionId, instanceData);
          this._initInstance(instanceData);
        }
      });

      document.addEventListener('shopify:section:unload', (event) => {
        const container = event.target.querySelector(selector);
        if (!container) return;

        const sectionId = container.getAttribute('data-section-id');
        const sectionMap = BaseSection.instances.get(this.constructor.name);

        if (sectionMap && sectionId) {
          const instanceData = sectionMap.get(sectionId);
          if (instanceData) {
            this.onUnload(instanceData);
            sectionMap.delete(sectionId);
          }
        }
      });
    }
  }

  // Expose the class globally for section rerenderer access
  static getInstance(sectionId) {
    for (const [, sectionMap] of BaseSection.instances) {
      if (sectionMap.has(sectionId)) {
        return sectionMap.get(sectionId);
      }
    }
    return null;
  }

  /**
   * Configures the error handler for the Vue app.
   *
   * @private
   * @param {Object} instanceData
   */
  _configureErrorMonitoring(instanceData) {
    const app = instanceData.container.__vue_app__;
    if (app) {
      app.config.errorHandler = vueToTrackJsErrorHandler;
    } else {
      DataDogLogs.error(
        `[Allbirds section] - BaseSection - _configureErrorMonitoring for ${instanceData.container.id}`,
        {},
        new Error('Vue app not found'),
      );
    }
  }

  /**
   * Initializes the section instance by calling the init method from the inherited class.
   *
   * @private
   * @param {Object} instanceData
   */
  _initInstance(instanceData) {
    const sectionId = instanceData.container.getAttribute('data-section-id');

    if (!instanceData.initialized) {
      this.init(instanceData);

      if (typeof this.setupInViewAnimationTimeline === 'function') {
        // Set up the in-view animation
        this.setupInViewAnimation(instanceData);
      }

      // Configure Vue error handler (after Vue app is created)
      this._configureErrorMonitoring(instanceData);

      // Register this section for rerender callbacks
      if (sectionId) {
        registerSectionRerenderCallback(sectionId, () => this.onRerenderComplete(instanceData));
      }

      // Mark as initialized
      instanceData.initialized = true;
    } else {
      DataDogLogs.warn(
        `[Allbirds section] - BaseSection - _initInstance skipped for ${sectionId} (already initialized)`,
      );
    }
  }

  /**
   * Returns the default options for in-view animations powered by ScrollTrigger
   * Child classes can override this method to provide section-specific animation trigger settings
   *
   * Common options include:
   * - start: String in format "element-position viewport-position" (e.g., "top 80%")
   * - end: String in format "element-position viewport-position" (e.g., "bottom 20%")
   * - once: Boolean that determines if animation should only play once
   * - toggleActions: String with four actions "onEnter onLeave onEnterBack onLeaveBack"
   * - markers: Boolean to show debug markers for trigger points
   * - scrub: Boolean or Number for scroll-synchronized animations
   *
   * @see https://greensock.com/docs/v3/Plugins/ScrollTrigger
   * @return {Object} ScrollTrigger configuration options
   */
  getInViewAnimationOptions() {
    return {
      end: 'bottom 20%',
      once: true,
      start: 'top 85%',
    };
  }

  /**
   * This method should be overriden by the child class to initialize the Vue app and pass the instance data.
   * The Vue app should be stored in container.__vue_app__ for proper cleanup.
   *
   * @abstract
   * @param {Object} instanceData
   */
  init(instanceData) {
    // Override in child class
    // The Vue app should be stored in container.__vue_app__ for proper cleanup
  }

  /**
   * Returns true if the template is a Ancillary Page template
   * Shopify Doc: https://shopify.dev/docs/api/liquid/objects/template
   *
   * @returns {boolean} True if the template is a page, false otherwise
   */
  isAncillaryPageTemplate() {
    return this.templateName === SHOPIFY_TEMPLATE_TYPES.PAGE;
  }

  /**
   * Returns true if the template is a Collection Page template
   * Shopify Doc: https://shopify.dev/docs/api/liquid/objects/template
   *
   * @returns {boolean} True if the template is collection, false otherwise
   */
  isCollectionPageTemplate() {
    return this.templateName === SHOPIFY_TEMPLATE_TYPES.COLLECTION;
  }

  /**
   * Returns true if the template is a Error Page template
   * Shopify Doc: https://shopify.dev/docs/api/liquid/objects/template
   *
   * @returns {boolean} True if the template is 404, false otherwise
   */
  isErrorPageTemplate() {
    return this.templateName === SHOPIFY_TEMPLATE_TYPES.NOT_FOUND_ERROR;
  }

  /**
   * Returns true if the template is Home Page template
   * Shopify Doc: https://shopify.dev/docs/api/liquid/objects/template
   *
   * @returns {boolean} True if the template is a index , false otherwise
   */
  isHomePageTemplate() {
    return this.templateName === SHOPIFY_TEMPLATE_TYPES.INDEX;
  }

  /**
   * Returns true if the template is a Product Page template
   * Shopify Doc: https://shopify.dev/docs/api/liquid/objects/template
   *
   * @returns {boolean} True if the template is product, false otherwise
   */
  isProductPageTemplate() {
    return this.templateName === SHOPIFY_TEMPLATE_TYPES.PRODUCT;
  }

  /**
   * Handles the completion of a section rerender by reinitializing the section
   * @param {Object} instanceData - The section instance data
   */
  onRerenderComplete(instanceData) {
    // Reset initialization flag to allow reinitialization
    instanceData.initialized = false;

    // Reinitialize the section
    this._initInstance(instanceData);
  }

  /**
   * This method should be overriden by the child class to cleanup the Vue app and any other resources.
   *
   * @abstract
   * @param {Object} instanceData
   */
  onUnload(instanceData) {
    const app = instanceData.container.__vue_app__;

    // Unregister from rerender callbacks
    const sectionId = instanceData.container.getAttribute('data-section-id');
    if (sectionId) {
      unregisterSectionRerenderCallback(sectionId);
    }

    // Kill ScrollTrigger first if it exists
    instanceData.animation?.scrollTrigger?.kill();

    // Then kill the animation if it exists
    if (instanceData.animation) {
      instanceData.animation.kill();
      instanceData.animation = null;
    }

    // Unmount the Vue app if it exists
    app?.unmount();
  }

  /**
   * Registers rerendering functionality for both single and multiple sections
   * @param {Object|Array<string>} target - Either instanceData (single) or array of section IDs (multiple)
   * @param {string|Object} options - Either section type (single) or section callbacks object (multiple)
   * @param {Function} onComplete - Callback function called after successful rerender
   * @param {Function} onError - Callback function called on error
   * @returns {Function} A function that can be called to trigger rerender
   */
  registerRerendering(target, options, onComplete, onError) {
    // If target is an array, treat as multiple sections
    if (Array.isArray(target)) {
      return () => rerenderSections(target, options, onComplete, onError);
    }

    // Otherwise, treat as single section (target is instanceData)
    const instanceData = target;
    return () => rerenderSections(instanceData.container, options, onComplete, onError);
  }

  /**
   * Registers rerendering functionality by section types, automatically resolving to section IDs
   * @param {Array<string>} sectionTypes - Array of section types to rerender
   * @param {Object} options - Section callbacks object (optional)
   * @param {Function} onComplete - Callback function called after successful rerender
   * @param {Function} onError - Callback function called on error
   * @returns {Function} A function that can be called to trigger rerender
   */
  registerRerenderingByType(sectionTypes, options = {}, onComplete, onError) {
    return () => rerenderSectionsByType(sectionTypes, options, onComplete, onError);
  }

  /**
   * Creates and initializes an animation timeline
   * This is a helper method used by setupInViewAnimation to create the timeline
   *
   * @param {Object} instanceData - The section instance data
   * @param {Function} setupAnimation - Function that returns a GSAP timeline for the animation
   * @return {Object} The created animation timeline
   */
  setupAnimation(instanceData, setupAnimation) {
    instanceData.animation = setupAnimation();

    if (instanceData.animation) {
      instanceData.animation.pause(0);
    }

    return instanceData.animation;
  }

  /**
   * Sets up a scroll-triggered animation that plays when the section comes into view
   * This initializes the animation with ScrollTrigger for visibility-based triggering
   *
   * @see https://greensock.com/docs/v3/Plugins/ScrollTrigger
   * @param {Object} instanceData - The section instance data
   */
  setupInViewAnimation(instanceData) {
    if (instanceData.animation?.scrollTrigger) {
      // Check if the ScrollTrigger needs to be refreshed
      if (instanceData.scrollTrigger) {
        ScrollTrigger.refresh();
      }
      return;
    }

    try {
      // Create the animation timeline
      const animation = this.setupAnimation(instanceData, () =>
        this.setupInViewAnimationTimeline(instanceData),
      );

      if (animation?.scrollTrigger) {
        // Store ScrollTrigger reference for cleanup
        instanceData.scrollTrigger = animation.scrollTrigger;
      }
    } catch (error) {
      DataDogLogs.warn(
        '[Allbirds section] - BaseSection - Error setting up animation timeline:',
        {},
        error,
      );
    }
  }

  /**
   * This method should be overriden by the child class to setup the animation timeline for when the section comes into view.
   * You should return a GSAP timeline with a scrollTrigger configuration.
   *
   * @abstract
   * @see https://greensock.com/docs/v3/Plugins/ScrollTrigger
   * @param {Object} instanceData - The section instance data
   * @return {Object} GSAP timeline with ScrollTrigger configuration
   *
   * @example
   * setupInViewAnimationTimeline(instanceData) {
   *   const container = instanceData.container;
   *
   *   // Create a timeline with ScrollTrigger
   *   const tl = gsap.timeline({
   *     scrollTrigger: {
   *       trigger: container,
   *       ...this.getInViewAnimationOptions(),
   *     },
   *   });
   *
   *   // Add animations to the timeline
   *   tl.to(container, { opacity: 1, duration: 1 });
   *
   *   return tl;
   * }
   */
  setupInViewAnimationTimeline(instanceData) {
    // Override in child class
    return null;
  }
}
